import { RubyGemMark } from '../../components/RubyGemMark';
import React from 'react';
import {
  ShieldCheck, CheckCircle2, AlertTriangle, XCircle, ExternalLink, Copy, Check,
  Tag, PlusCircle,
} from 'lucide-react';
import { api } from '../../services/apiService';
import type { SubscriptionSummary } from '../../types';
import {
  billingApi, type LiveSubscriptionRow, type BillingSkuRow, type SoldLocation,
} from '../../services/billingApi';
import { groupByStore, isAddOn, type StoreBill } from './locationBilling';
import { hardwareApi } from '../../services/hardwareApi';
import { getIcon } from '../../services/iconRegistry';
import { resolveAccent, tint } from '../../utils/accentColor';
import ShimmerImage from '../../components/ShimmerImage';

/**
 * VERIFY A SUBSCRIPTION AGAINST STRIPE, ONE AT A TIME, ON DEMAND.
 *
 * The Payment-verified stage exists because "Stripe says paid" and "somebody
 * looked" are different facts. Until now the looking meant leaving for the
 * Stripe dashboard, and the figures on this screen came from
 * `tenant.subscriptionCost` — a single price super-admin stamps from
 * `items.data[0]`. Under per-item billing that is one line of thirteen, so the
 * panel reported "software $39.99" for an account billing $165.91.
 *
 * NOTHING IS FETCHED UNTIL ASKED. A subscription's items and invoices are a
 * Stripe round-trip each, an order can carry one per location, and the
 * overwhelming majority of visits to this stage never need them. So the rows
 * render from what the list read already — id, status, item count, the real
 * monthly — and the detail arrives only when an operator presses Verify.
 *
 * WHAT COUNTS AS VERIFIED is deliberately narrow: the items sum to what we
 * expect, and the money arrived. Anything else is SHOWN, never smoothed over —
 * this stage's whole purpose is catching the case where the two disagree.
 */

const money = (cents?: number) =>
  typeof cents === 'number' ? `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';

/** Live statuses. A cancelled subscription is not a discrepancy to reconcile. */
const HEALTHY_STATUSES = new Set(['active', 'trialing']);

/**
 * A subscription item's own monthly amount.
 *
 * `unit_amount * quantity`, because an item may carry more than one of a thing
 * and the price is per unit. Discounts are NOT subtracted here: they are shown
 * on their own row, since a promotion that ends is a fact an operator reading
 * this next month needs to see separately from the settled price.
 */
const itemMonthlyCents = (item: NonNullable<SubscriptionSummary['items']>['data'][number]): number =>
  (item.price?.unit_amount || 0) * (item.quantity || 1);

/**
 * The promotion on an item, and when it stops.
 *
 * `discounts` arrives from Stripe as bare `di_…` ids; super-admin's
 * `getSubscription` attaches the coupon (see the note there) because the id
 * alone explains nothing — and an unexplained promotion on this screen is
 * indistinguishable from money that went missing.
 *
 * `end` is the discount's own expiry, which beats "3 months" from the coupon:
 * it is a real date on the customer's calendar rather than a duration the
 * reader has to add to a start they cannot see.
 */
const couponOf = (item: NonNullable<SubscriptionSummary['items']>['data'][number]) => {
  for (const d of item.discounts || []) {
    if (typeof d === 'object' && d?.coupon) {
      return { ...d.coupon, end: (d as any).end as number | undefined };
    }
  }
  return undefined;
};

const endsOn = (epochSeconds?: number) =>
  epochSeconds
    ? new Date(epochSeconds * 1000).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : undefined;

const CopyChip: React.FC<{ value: string; href?: string }> = ({ value, href }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          }).catch(() => {});
        }}
        title={`Copy ${value}`}
        className="inline-flex items-center gap-1.5 rounded-md border border-stone-200 bg-stone-100 px-1.5 py-0.5
          font-mono text-[11px] text-stone-600 hover:bg-stone-200 hover:text-stone-900"
      >
        {value.length > 26 ? `${value.slice(0, 24)}…` : value}
        {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
      </button>
      {href && (
        <a href={href} target="_blank" rel="noopener noreferrer" title="Open in Stripe"
          className="text-stone-400 hover:text-stone-700">
          <ExternalLink size={12} />
        </a>
      )}
    </span>
  );
};

/**
 * WHAT AN ITEM IS, resolved from the three catalogues that own the answer.
 *
 * A Stripe item carries a price, a quantity and a `skuId` — and nothing a
 * person recognises. `sku_pkg_mmp52vus_5xf9r3` is not a package name, and
 * thirteen of them stacked up is a wall of hashes on the screen where somebody
 * decides whether to release stock.
 *
 * Three hops, each from the service that actually owns the fact:
 *   billing catalogue  skuId → name, and packageId / modelId
 *   packages           packageId → icon and colour (billing SKUs carry neither)
 *   device models      modelId → the photograph and the model name
 *
 * Loaded ONCE, on the first Verify, and shared by every row after it — the
 * catalogues are account-wide, so fetching them per subscription would be three
 * more round-trips for the same three answers.
 */
interface Catalogs {
  skuById: Map<string, BillingSkuRow>;
  packageById: Map<string, any>;
  modelById: Map<string, { modelName: string; imageUrl?: string }>;
}

interface ResolvedItem {
  key: string;
  label: string;
  sub?: string;
  monthlyCents: number;
  /** `end` is the discount's own expiry — see `couponOf`. */
  coupon?: { amount_off?: number; duration_in_months?: number; end?: number };
  /** In the offer, bought on top, or unknown — see `resolveItem`. */
  inDeal?: boolean;
  /** What WE sent to Stripe for this sku, from the completion's stored lines. */
  sentCents?: number;
  /** What the pinned deal version promised for it. */
  offer?: { introPriceCents: number; introMonths: number };
  /** A package's own colour and icon, when this item is one. */
  accent?: { band: string; ink: string; onWhite: string; from: string };
  Icon?: React.ComponentType<any>;
  imageUrl?: string;
}

/** One invoice as this panel reports it — the subset both callers already have. */
export interface VerifyInvoice {
  id: string;
  status: string;
  amount: number;
  amountPaid: number;
  periodStart?: number;
  hostedInvoiceUrl?: string;
}

const SubscriptionVerifyList: React.FC<{
  subscriptions: LiveSubscriptionRow[];
  /** Every invoice on the tenant's customer — already read by the parent. */
  invoices: VerifyInvoice[] | null;
  /** Location names, so a row is not a bare `loc_…`. */
  locationNames?: Record<string, string>;
  /** The account these stores belong to — named before them, never instead of them. */
  businessName?: string;
  /** WHAT WE SENT to Stripe, per location — the completion's own stored lines. */
  soldLocations?: SoldLocation[];
  /**
   * The PINNED deal version this order was sold on.
   *
   * `packageIntros` is keyed by `packageId`, which reaches a subscription item
   * through the billing SKU's `packageId` — the same hop the item names use.
   */
  dealVersion?: {
    versionNo?: number;
    packageIntros?: Record<string, { introPriceCents: number; introMonths: number }>;
    packageDiscounts?: Record<string, any>;
    deviceTerms?: Record<string, any>;
    /*
      WHAT THE OFFER COVERS. Ruling 2026-09-06: the deal's price holds for its
      own lines and anything bought on top prices at list — so "in the deal" and
      "add-on" bill differently, and an operator checking an unexpected figure
      needs to know which kind of line they are looking at before they can tell
      an unusual one from a wrong one.
    */
    packageIds?: string[];
    deviceSkuIds?: string[];
  } | null;
  stripeBase: string;
  /** This order's store — its group opens first; the others fold away. */
  homeLocationId?: string;
  /** The subscription this order started, marked in its group. */
  orderSubscriptionId?: string;
}> = ({ subscriptions, invoices, locationNames, businessName, soldLocations, dealVersion, stripeBase, homeLocationId, orderSubscriptionId }) => {
  const [open, setOpen] = React.useState<Record<string, SubscriptionSummary>>({});
  const [busy, setBusy] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState<Record<string, string>>({});
  const [catalogs, setCatalogs] = React.useState<Catalogs | null>(null);
  /** So a second Verify does not re-fetch three account-wide catalogues. */
  const catalogsLoading = React.useRef<Promise<Catalogs | null> | null>(null);

  /**
   * The three catalogues, fetched once and only when a row is opened.
   *
   * Every one may fail on its own and none of them may stop the verification:
   * an operator checking whether the money landed needs the AMOUNTS, and a
   * missing photograph or a package colour is cosmetic beside that. A hop that
   * fails simply leaves the item rendering as its skuId, exactly as it did
   * before any of this existed.
   */
  const loadCatalogs = React.useCallback((): Promise<Catalogs | null> => {
    if (catalogs) return Promise.resolve(catalogs);
    if (catalogsLoading.current) return catalogsLoading.current;
    const p = Promise.all([
      billingApi.listSkus().catch(() => null),
      api.packages.listVisible().catch(() => null),
      hardwareApi.listModels().catch(() => null),
    ]).then(([skus, pkgs, models]) => {
      const next: Catalogs = {
        skuById: new Map((skus?.items || []).map((s) => [s.skuId, s] as const)),
        packageById: new Map(((pkgs as any)?.packages || []).map((p: any) => [p.id, p] as const)),
        modelById: new Map(((models as any)?.items || []).map((m: any) => [
          m.modelId,
          { modelName: m.modelName, imageUrl: m.media?.heroImageUrl },
        ] as const)),
      };
      setCatalogs(next);
      return next;
    }).catch(() => null);
    catalogsLoading.current = p;
    return p;
  }, [catalogs]);

  const verify = async (subscriptionId: string) => {
    setBusy(subscriptionId);
    setFailed((f) => ({ ...f, [subscriptionId]: '' }));
    try {
      // Together: the names arrive with the figures rather than a beat after
      // them, so a row never renders as hashes and then re-renders as words.
      const [sub] = await Promise.all([
        api.superAdmin.getSubscription(subscriptionId),
        loadCatalogs(),
      ]);
      setOpen((o) => ({ ...o, [subscriptionId]: sub }));
    } catch (e: any) {
      /*
        NAMED, NEVER SILENT. An operator who presses Verify and sees nothing
        cannot tell "Stripe is down" from "this subscription is fine", and the
        two lead to opposite decisions about releasing stock.
      */
      setFailed((f) => ({ ...f, [subscriptionId]: e?.message || 'Stripe could not be read just now.' }));
    } finally {
      setBusy(null);
    }
  };

  /**
   * One Stripe item, made recognisable.
   *
   * Falls through to the skuId at every step rather than to a placeholder: an
   * id an operator can paste into Stripe beats "Unknown item", and it is what
   * this list showed before the catalogues were wired in.
   */
  const resolveItem = (
    it: NonNullable<SubscriptionSummary['items']>['data'][number],
    idx: number,
    sent?: Map<string, { amountCents: number; quantity: number }>,
  ): ResolvedItem => {
    const skuId = it.metadata?.skuId || it.price?.metadata?.skuId || '';
    const sku = skuId ? catalogs?.skuById.get(skuId) : undefined;
    /*
      THE THREE FACTS ABOUT ONE LINE, from the three records that hold them:

        sentCents  what WE sent to Stripe   (the completion's stored lines)
        offer      what the DEAL promised   (the pinned version's terms)
        the item   what Stripe is billing   (this object)

      Joined on `skuId`, and for the offer through the billing SKU's own
      `packageId` — `packageIntros` is keyed by package, not by sku.
    */
    const sentCents = skuId ? sent?.get(skuId)?.amountCents : undefined;
    const intro = sku?.packageId ? dealVersion?.packageIntros?.[sku.packageId] : undefined;

    /*
      IS THIS LINE PART OF THE OFFER, or bought on top of it?

      Software is matched by PACKAGE id and hardware by SKU id, because that is
      how each is named on the deal. `undefined` when the deal names neither
      list — an unknown, which must not be drawn as "add-on": telling an
      operator a deal package is an extra is the same class of wrong number as
      the ones this screen exists to catch.
    */
    const dealPackages = dealVersion?.packageIds;
    const dealDevices = dealVersion?.deviceSkuIds;
    const inDeal: boolean | undefined = sku?.modelId
      ? (dealDevices ? dealDevices.includes(skuId) : undefined)
      : (dealPackages && sku?.packageId ? dealPackages.includes(sku.packageId) : undefined);

    const base: ResolvedItem = {
      key: it.id || `${skuId}_${idx}`,
      label: sku?.name || it.price?.nickname || skuId || it.price?.id || 'Item',
      monthlyCents: itemMonthlyCents(it),
      coupon: couponOf(it),
      inDeal,
      sentCents,
      offer: intro
        ? { introPriceCents: intro.introPriceCents, introMonths: intro.introMonths }
        : undefined,
    };

    // A DEVICE — the photograph is what an operator actually recognises, and
    // the term is what the money means.
    if (sku?.modelId) {
      const model = catalogs?.modelById.get(sku.modelId);
      const term = sku.termMonths ? `${sku.termMonths} months` : undefined;
      const mode = sku.saleMode === 'rental' ? 'Rental'
        : sku.saleMode === 'installment' ? 'Financed'
          : sku.saleMode === 'lease' ? 'Lease' : undefined;
      return {
        ...base,
        label: model?.modelName || sku.name || base.label,
        sub: [mode, term].filter(Boolean).join(' · ') || undefined,
        imageUrl: model?.imageUrl,
      };
    }

    // A PACKAGE — painted in its own colour, exactly as the Packages page
    // paints it, so the same product looks the same wherever it appears.
    if (sku?.packageId) {
      const pkg = catalogs?.packageById.get(sku.packageId);
      if (pkg) {
        const a = resolveAccent(pkg);
        return {
          ...base,
          label: pkg.name || sku.name || base.label,
          accent: a as any,
          Icon: getIcon(pkg.icon || ''),
        };
      }
    }
    return base;
  };

  if (subscriptions.length === 0) return null;

  /*
    BY STORE, NOT ONE LIST.

    Seven subscriptions in a row under "one per location" read as seven stores,
    when it was three stores, each with its plan and an add-on, and one nobody
    had placed. A merchant pays per store, so each store is a group: its plan
    first, its add-ons under it, its own monthly on the heading. This order's
    store is open; the rest fold away with their total showing.
  */
  const stores = groupByStore(subscriptions, locationNames || {}, homeLocationId);
  const homeStore = homeLocationId ? stores.find((g) => g.locationId === homeLocationId) : undefined;
  const others = homeStore ? stores.filter((g) => g !== homeStore) : stores;
  const placedStores = stores.filter((g) => g.locationId).length;
  const renderGroup = (g: StoreBill, row: (s: LiveSubscriptionRow) => React.ReactNode) => (
    <div key={g.locationId || 'none'} className="rounded-lg border border-stone-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 bg-stone-50 border-b border-stone-200">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12.5px] font-bold text-stone-900">{g.name}</span>
          {g === homeStore && (
            <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700">this order&apos;s store</span>
          )}
          {!g.locationId && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">needs a store</span>
          )}
        </div>
        <span className="text-[12.5px] font-bold tabular-nums text-stone-900">{money(g.monthlyCents)}/mo</span>
      </div>
      <div className="divide-y divide-stone-100 px-3">{g.subs.map(row)}</div>
    </div>
  );
  const renderGrouped = (row: (s: LiveSubscriptionRow) => React.ReactNode) => (
    <div className="space-y-2.5">
      {homeStore && renderGroup(homeStore, row)}
      {homeStore && others.length > 0 ? (
        <details className="group/others">
          <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-2 rounded-lg border border-dashed border-stone-300 px-3 text-[12px] text-stone-600 hover:bg-stone-50">
            <span>Other stores on this account · {others.length}</span>
            <span className="tabular-nums text-stone-400">{money(others.reduce((n, g) => n + g.monthlyCents, 0))}/mo</span>
          </summary>
          <div className="mt-2.5 space-y-2.5">{others.map((g) => renderGroup(g, row))}</div>
        </details>
      ) : (
        !homeStore && others.map((g) => renderGroup(g, row))
      )}
      <div className="flex items-center justify-between border-t border-stone-100 pt-2 text-[12px]">
        <span className="text-stone-500">Whole account, every store</span>
        <span className="font-bold tabular-nums text-stone-900">{money(stores.reduce((n, g) => n + g.monthlyCents, 0))}/mo</span>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3.5">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h4 className="text-[13px] font-bold text-stone-900">Subscriptions on this account</h4>
        <span className="text-[11px] text-stone-400">
          {subscriptions.length} subscription{subscriptions.length === 1 ? '' : 's'} · {placedStores} store{placedStores === 1 ? '' : 's'}
          {stores.length > placedStores ? ' + 1 unplaced' : ''}
        </span>
      </div>
      <p className="text-[11.5px] text-stone-500 mb-2">
        Nothing is read from Stripe until you ask for it.
      </p>

      {renderGrouped((s) => {
          const sub = open[s.subscriptionId];
          const loading = busy === s.subscriptionId;
          const error = failed[s.subscriptionId];
          const healthy = HEALTHY_STATUSES.has(s.status);

          /*
            THE INVOICES THIS SUBSCRIPTION RAISED.

            Matched on the invoice's own `subscription` field where Stripe sends
            one. A one-time invoice (deposits, down payments) belongs to no
            subscription and is deliberately excluded — it is money on the
            ACCOUNT, reconciled by the panel above, and counting it here would
            make every subscription look overpaid.
          */
          const mine = (invoices || []).filter((i) => (i as any).subscription === s.subscriptionId);
          const paidCents = mine.filter((i) => i.status === 'paid').reduce((n, i) => n + (i.amountPaid || 0), 0);
          const unpaid = mine.filter((i) => i.status !== 'paid' && (i.amount || 0) > 0);

          /*
            WHAT WE SENT for THIS location.

            Matched on `subscriptionId` first — the completion records the
            subscription each location produced, so that is exact — and on
            `locationId` only as a fallback, for a record written before the id
            was stored.
          */
          const soldHere = (soldLocations || []).find(
            (l) => (l.subscriptionId && l.subscriptionId === s.subscriptionId)
              || (!isAddOn(s) && !!s.locationId && l.locationId === s.locationId),
          );
          const sentBySku = new Map(
            (soldHere?.lines || []).map((l) => [l.skuId, { amountCents: l.amountCents, quantity: l.quantity }] as const),
          );
          const sentMonthly = (soldHere?.lines || []).reduce((n, l) => n + l.amountCents * (l.quantity || 1), 0);

          const items = sub?.items?.data || [];
          const stripeMonthly = items.reduce((n, i) => n + itemMonthlyCents(i), 0);
          /** Our record against Stripe's, at the whole-subscription level. */
          const sentDrift = soldHere ? stripeMonthly - sentMonthly : undefined;
          /*
            The promotions running on this subscription right now — the bridge
            between what the items LIST at and what the card is actually
            charged. Summed from the coupons themselves rather than inferred
            from the difference, so the verdict explains the gap instead of
            merely restating it.
          */
          const runningCoupons = items.map(couponOf).filter(Boolean) as Array<{ amount_off?: number }>;
          const promoOffCents = runningCoupons.reduce((n, c) => n + (c.amount_off || 0), 0);
          const promoCount = runningCoupons.length;
          const expected = s.monthlyCents;
          // Compared only when BOTH are known — a missing figure is not a match.
          const drift = sub && typeof expected === 'number' ? stripeMonthly - expected : undefined;

          return (
            <div key={s.subscriptionId} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {/*
                    THE BUSINESS, THEN THE STORE.

                    A row headed `loc_1788625971118` says nothing an operator
                    can act on. The account is named first because that is what
                    they came here about, the store second because that is which
                    subscription this is — and the id is kept underneath rather
                    than dropped, since it is what they paste into Stripe when
                    the names are not enough.
                  */}
                  <div className="flex items-baseline gap-2 flex-wrap">
                    {businessName && (
                      <span className="text-[13.5px] font-bold text-stone-900">{businessName}</span>
                    )}
                    <span className={businessName
                      ? 'text-[12.5px] text-stone-500'
                      : 'text-[13.5px] font-bold text-stone-900'}>
                      {businessName && <span className="text-stone-300 mr-1.5">·</span>}
                      {s.subscriptionId === orderSubscriptionId ? 'Add-on · this order'
                        : isAddOn(s) ? 'Add-on'
                        : 'Store plan'}
                    </span>
                    <span className={`text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                      healthy
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap text-[11.5px] text-stone-500">
                    <CopyChip
                      value={s.subscriptionId}
                      href={`${stripeBase}/subscriptions/${s.subscriptionId}`}
                    />
                    {typeof s.itemCount === 'number' && <span>{s.itemCount} items</span>}
                    {typeof s.monthlyCents === 'number' && <span>· {money(s.monthlyCents)}/mo</span>}
                  </div>
                </div>

                {!sub && (
                  <button
                    type="button"
                    onClick={() => void verify(s.subscriptionId)}
                    disabled={loading}
                    className="shrink-0 inline-flex items-center gap-2 rounded-[10px] border border-[#DC143C] bg-white
                      px-3.5 min-h-[40px] text-[12.5px] font-bold text-[#DC143C] hover:bg-rose-50 disabled:opacity-55"
                  >
                    {loading
                      ? <><RubyGemMark size={14} drawing color="currentColor" /> Reading Stripe…</>
                      : <><ShieldCheck size={14} /> Verify with Stripe</>}
                  </button>
                )}
              </div>

              {error && (
                <p className="mt-2 text-[11.5px] text-amber-700 inline-flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {error}
                </p>
              )}

              {sub && (
                <div className="mt-3 space-y-2">
                  {/* ── Stripe's own items ── */}
                  <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-3">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-stone-400 mb-2">
                      {items.length} subscription item{items.length === 1 ? '' : 's'}
                    </p>
                    <div className="divide-y divide-stone-200/70">
                      {items.map((it, idx) => {
                        const r = resolveItem(it, idx, sentBySku);
                        /*
                          DOES STRIPE MATCH WHAT WE SENT?

                          The list price is the comparable figure — the promo is
                          a coupon laid over it, and is checked separately by
                          `offerMatches` below. A line we never sent (or a sku
                          the record does not name) is not a mismatch, it is an
                          unknown, and it says so rather than accusing.
                        */
                        const sentMatches = typeof r.sentCents === 'number'
                          ? r.sentCents === r.monthlyCents
                          : undefined;
                        // The deal promised an intro; Stripe should carry a
                        // coupon worth exactly the gap, for exactly as long.
                        const offerMatches = r.offer
                          ? !!r.coupon
                            && (r.coupon.amount_off || 0) === r.monthlyCents - r.offer.introPriceCents
                            && (r.coupon.duration_in_months || 0) === r.offer.introMonths
                          : !r.coupon;
                        return (
                          <div key={r.key} className="py-2 flex items-center justify-between gap-3">
                            <div className="min-w-0 flex items-center gap-2.5">
                              {/* THE THING ITSELF — a device's photograph, a
                                  package's own icon in its own colour, and a
                                  neutral square when neither could be resolved.
                                  Same size throughout so the list stays a list. */}
                              {r.imageUrl !== undefined || r.accent ? null : (
                                <span className="w-8 h-8 rounded-lg bg-stone-200/70 shrink-0" />
                              )}
                              {r.imageUrl !== undefined && (
                                <ShimmerImage
                                  src={r.imageUrl}
                                  alt={r.label}
                                  width={32}
                                  height={32}
                                  objectFit="contain"
                                  fallbackBgColor="bg-stone-100"
                                  className="rounded-lg shrink-0"
                                />
                              )}
                              {r.accent && r.Icon && (
                                <span
                                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ background: tint(r.accent.from, 0.16) }}
                                >
                                  <r.Icon size={15} style={{ color: r.accent.onWhite }} />
                                </span>
                              )}
                              <div className="min-w-0">
                                <div className="text-[12.5px] text-stone-800 truncate flex items-center gap-1.5">
                                  <span className="truncate">
                                    {r.label}
                                    {it.quantity && it.quantity > 1 && (
                                      <span className="text-[11px] text-stone-400"> ×{it.quantity}</span>
                                    )}
                                  </span>
                                  {/* Drawn only when the deal actually SAYS —
                                      `undefined` is an unknown, and labelling a
                                      deal package "add-on" would be the same
                                      class of wrong number this screen catches. */}
                                  {r.inDeal !== undefined && (
                                    <span className={`shrink-0 inline-flex items-center gap-1 text-[9.5px] font-extrabold uppercase tracking-wider px-1.5 py-px rounded-full border ${
                                      r.inDeal
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}>
                                      {/* A TAG for what the offer covers, a PLUS
                                          for what was added on top — the shape
                                          says which before the word is read, and
                                          on a list of thirteen that is the
                                          difference between scanning and
                                          reading. */}
                                      {r.inDeal ? <Tag size={9} strokeWidth={3} /> : <PlusCircle size={9} strokeWidth={3} />}
                                      {r.inDeal ? 'in deal' : 'add-on'}
                                    </span>
                                  )}
                                </div>
                                {r.sub && <div className="text-[11px] text-stone-400">{r.sub}</div>}
                                {/*
                                  WHAT WE SENT, AND WHAT THE DEAL PROMISED —
                                  under the name, beside the price Stripe holds.
                                  Silent when everything agrees: three matching
                                  figures repeated on every one of thirteen rows
                                  is noise that hides the one row that differs.
                                */}
                                {sentMatches === false && (
                                  <div className="text-[11px] font-semibold text-amber-700">
                                    we sent {money(r.sentCents)}
                                  </div>
                                )}
                                {sentMatches === undefined && (
                                  <div className="text-[11px] text-stone-400">not in our record</div>
                                )}
                                {r.offer && (
                                  <div className={`text-[11px] ${offerMatches ? 'text-stone-400' : 'text-amber-700 font-semibold'}`}>
                                    deal v{dealVersion?.versionNo}: {money(r.offer.introPriceCents)} for {r.offer.introMonths} mo
                                    {!offerMatches && (r.coupon ? ' — Stripe differs' : ' — no coupon in Stripe')}
                                  </div>
                                )}
                                {!r.offer && r.coupon && (
                                  <div className="text-[11px] font-semibold text-amber-700">
                                    discounted in Stripe, but the deal promises no promotion here
                                  </div>
                                )}
                              </div>
                            </div>
                            {/*
                              WHAT IT COSTS NOW, AND WHAT IT GOES BACK TO.

                              A promoted line showed only its list price, so a
                              subscription whose items totalled $302.89 charged
                              $250.41 with nothing on the page accounting for
                              the $52.48. The line now leads with what is
                              ACTUALLY billed today, strikes the price it will
                              return to, and dates the return — three facts an
                              operator needs to tell a promotion from a fault.
                            */}
                            <div className="shrink-0 text-right">
                              {r.coupon ? (
                                <>
                                  <span className="text-[11.5px] text-stone-400 line-through tabular-nums mr-1.5">
                                    {money(r.monthlyCents)}
                                  </span>
                                  <span className="text-[12.5px] font-semibold text-emerald-700 tabular-nums">
                                    {money(Math.max(0, r.monthlyCents - (r.coupon.amount_off || 0)))}
                                  </span>
                                  <div className="text-[10.5px] text-emerald-600">
                                    −{money(r.coupon.amount_off)}
                                    {endsOn(r.coupon.end)
                                      ? ` until ${endsOn(r.coupon.end)}`
                                      : r.coupon.duration_in_months ? ` · ${r.coupon.duration_in_months} mo` : ''}
                                  </div>
                                </>
                              ) : (
                                <span className="text-[12.5px] font-semibold text-stone-900 tabular-nums">
                                  {money(r.monthlyCents)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── The invoices it raised ── */}
                  {mine.length > 0 && (
                    <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-3">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-stone-400 mb-2">
                        Invoices
                      </p>
                      <div className="divide-y divide-stone-200/70">
                        {mine.map((i) => (
                          <div key={i.id} className="py-1.5 flex items-center justify-between gap-3">
                            <CopyChip value={i.id} href={`${stripeBase}/invoices/${i.id}`} />
                            <div className="flex items-center gap-2.5 shrink-0">
                              <span className={`text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${
                                i.status === 'paid'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {i.status}
                              </span>
                              <span className="text-[12.5px] font-semibold text-stone-900 tabular-nums">
                                {money(i.status === 'paid' ? i.amountPaid : i.amount)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/*
                    ── THE VERDICT ──

                    AMBER for a mismatch, RED only for money that did not arrive.
                    They are different problems: a figure that disagrees needs a
                    person to look, an unpaid invoice means stock must not leave.
                    Painting the first one red would make every rounding
                    difference read as an incident and teach an operator to skim
                    past the colour that matters.
                  */}
                  {unpaid.length > 0 ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2.5">
                      <XCircle size={16} className="text-red-600 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] text-red-800">
                        <strong className="block">
                          {unpaid.length === 1 ? 'An invoice has not been paid.' : `${unpaid.length} invoices have not been paid.`}
                        </strong>
                        {money(unpaid.reduce((n, i) => n + (i.amount || 0), 0))} outstanding.
                        Nothing should be picked against this order.
                      </div>
                    </div>
                  ) : sentDrift !== undefined && sentDrift !== 0 ? (
                    /*
                      THE CHECK THAT MATTERS MOST, and the reason this panel
                      exists: Stripe is billing something other than what we
                      sent it. Ranked above the server-sum comparison below,
                      which is two views of Stripe agreeing with each other.
                    */
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
                      <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] text-amber-900">
                        <strong className="block">
                          Stripe bills {money(Math.abs(sentDrift))} {sentDrift > 0 ? 'more' : 'less'} than we sent.
                        </strong>
                        We sent {money(sentMonthly)} across {(soldHere?.lines || []).length} lines · Stripe holds{' '}
                        {money(stripeMonthly)} across {items.length}.
                      </div>
                    </div>
                  ) : drift !== undefined && drift !== 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2.5">
                      <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                      <div className="text-[12.5px] text-amber-900">
                        <strong className="block">
                          Stripe bills {money(Math.abs(drift))} {drift > 0 ? 'more' : 'less'} than we expect.
                        </strong>
                        Stripe {money(stripeMonthly)} · RubyTill {money(expected)} across {items.length} items.
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2.5">
                      <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
                      {/*
                        SAY WHICH MONEY THIS IS.

                        It read "Items sum to $302.89 and $250.41 has been
                        collected", beside a Transactions panel totalling
                        $700.40 — so it looked like $450 had gone missing. Both
                        figures were right and neither said what it was OF: the
                        sum is the SETTLED month, the collection is only this
                        subscription's own invoices, and the deposits invoice
                        belongs to the customer rather than to any subscription.
                        A verdict on money has to name its own scope.
                      */}
                      {/*
                        THE ARITHMETIC, WRITTEN OUT.

                        "Items sum to $302.89 and $250.41 has been collected"
                        was two true figures and no bridge between them, which
                        reads as a $52.48 discrepancy — the exact thing this
                        stage exists to surface, raised falsely. When the gap is
                        promotions, SAY the promotions and show the subtraction;
                        a difference an operator has to derive is a difference
                        they will eventually escalate.
                      */}
                      <div className="text-[12.5px] text-emerald-900">
                        <strong className="block">
                          Verified{soldHere ? ' against what we sent' : ''}.
                        </strong>
                        Its {items.length} items list at {money(stripeMonthly)} a month
                        {soldHere ? ', exactly what we sent' : ''}
                        {dealVersion?.versionNo ? ` on deal v${dealVersion.versionNo}` : ''}.
                        {promoOffCents > 0 && (
                          <> {promoCount} promotion{promoCount === 1 ? '' : 's'} take
                            {promoCount === 1 ? 's' : ''} {money(promoOffCents)} off, so it bills{' '}
                            <strong>{money(stripeMonthly - promoOffCents)}</strong> today.</>
                        )}
                        {paidCents > 0 && (
                          <> {money(paidCents)} collected on this subscription so far.</>
                        )}
                        {' '}Nothing outstanding.
                        {paidCents > 0 && (
                          <span className="block mt-0.5 text-emerald-700/80">
                            One-time charges — deposits and down payments — sit on their own
                            invoice and are counted under Transactions, not here.
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default SubscriptionVerifyList;
